import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2, Cable, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { apiFetch, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { RelativeTime } from '@/components/app/relative-time';
import { RequestStatusWord } from '@/components/app/requests-table';
import { licenseRequestSchema, type LicenseRequest } from '@/schemas/license-requests';

async function fetchRequest(id: string): Promise<LicenseRequest> {
  const resp = await apiFetch(`/license-requests/${id}`);
  const json = await resp.json();
  return licenseRequestSchema.parse(json);
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <dt className="text-[12.5px] uppercase tracking-wide text-faint">{label}</dt>
      <dd className="text-[14.5px] text-foreground">{children}</dd>
    </div>
  );
}

function TimelineItem({
  done,
  title,
  at,
  tone = 'default',
}: {
  done: boolean;
  title: string;
  at: string | null;
  tone?: 'default' | 'danger';
}) {
  return (
    <li className="flex items-start gap-3">
      <span
        aria-hidden="true"
        className={`mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
          done
            ? tone === 'danger'
              ? 'border-status-revoked bg-status-revoked text-background'
              : 'border-status-active bg-status-active text-background'
            : 'border-border bg-transparent'
        }`}
      >
        {done && <Check className="h-3 w-3" aria-hidden="true" />}
      </span>
      <div className="space-y-0.5">
        <p className="text-[14px] font-medium text-foreground">{title}</p>
        {at && (
          <p className="text-[12.5px] text-muted-foreground">
            <RelativeTime date={at} />
          </p>
        )}
      </div>
    </li>
  );
}

export default function RequestDetailPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['license-requests', 'detail', id],
    queryFn: () => fetchRequest(id),
    enabled: id.length > 0,
    staleTime: 15_000,
  });

  const offlineMutation = useMutation({
    mutationFn: async () => {
      const resp = await apiFetch(`/license-requests/${id}/request-offline`, { method: 'POST' });
      return resp.json();
    },
    onSuccess: () => {
      toast.success(t('requests.detail.offlineToast'));
      void queryClient.invalidateQueries({ queryKey: ['license-requests'] });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        toast.error(t('requests.detail.offlineConflict'));
      } else if (err instanceof ApiError && err.status === 403) {
        toast.error(t('errors.accessDenied'));
      } else {
        toast.error(t('errors.network'));
      }
    },
  });

  if (query.isLoading) {
    return (
      <div className="mx-auto w-full max-w-[860px] space-y-6">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-10 w-72" />
        <div className="grid grid-cols-2 gap-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12" />
          ))}
        </div>
      </div>
    );
  }

  if (query.isError) {
    const isForbidden = query.error instanceof ApiError && query.error.status === 403;
    return (
      <div className="mx-auto flex w-full max-w-[860px] flex-col items-center gap-4 py-20 text-center">
        <p className="text-[15px] text-destructive">
          {isForbidden ? t('errors.accessDenied') : t('requests.detail.loadError')}
        </p>
        <div className="flex gap-2">
          {!isForbidden && (
            <Button variant="outline" size="sm" onClick={() => query.refetch()}>
              {t('common.retry')}
            </Button>
          )}
          <Button asChild variant="ghost" size="sm">
            <Link to="/requests">{t('requests.detail.back')}</Link>
          </Button>
        </div>
      </div>
    );
  }

  const req = query.data;
  if (!req) return null;

  const canRequestOffline = req.status !== 'rejected' && !req.offlineRequested;
  const reviewed = req.status !== 'submitted';

  return (
    <div className="mx-auto w-full max-w-[860px]">
      <Button asChild variant="ghost" size="sm" className="-ml-2 mb-4">
        <Link to="/requests">
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
          {t('requests.detail.back')}
        </Link>
      </Button>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-mono text-2xl font-semibold tracking-tight text-foreground">
            {req.serial}
          </h1>
          <p className="mt-1.5 text-[14.5px] text-muted-foreground">{req.buyerCompany}</p>
        </div>
        <RequestStatusWord status={req.status} rejectionReason={req.rejectionReason} />
      </div>

      <dl className="mt-8 grid grid-cols-1 gap-5 rounded-lg border border-border p-5 sm:grid-cols-2">
        <Field label={t('requests.detail.type')}>
          {req.configuration.type ? t(`common.type.${req.configuration.type}`) : '—'}
        </Field>
        <Field label={t('requests.detail.maxActivations')}>
          <span className="tabular-nums">{req.configuration.maxActivations ?? '—'}</span>
        </Field>
        <Field label={t('requests.detail.entitlements')}>
          {req.configuration.entitlements && req.configuration.entitlements.length > 0 ? (
            <span className="flex flex-wrap gap-1.5">
              {req.configuration.entitlements.map((e) => (
                <Badge key={`${e.module}-${e.tier}`} variant="secondary">
                  {e.module} · {e.tier}
                </Badge>
              ))}
            </span>
          ) : (
            '—'
          )}
        </Field>
        <Field label={t('requests.detail.created')}>
          <RelativeTime date={req.createdAt} />
        </Field>
        {req.status === 'rejected' && req.rejectionReason && (
          <div className="sm:col-span-2">
            <Field label={t('requests.detail.rejectionReason')}>
              <span className="text-status-revoked">{req.rejectionReason}</span>
            </Field>
          </div>
        )}
        {req.status === 'approved' && req.resultingLicenseId && (
          <div className="sm:col-span-2">
            <Field label={t('requests.detail.resultingLicense')}>
              <span className="font-mono text-[13px]">{req.resultingLicenseId}</span>
            </Field>
          </div>
        )}
      </dl>

      {/* Status timeline */}
      <section className="mt-8" aria-label={t('requests.detail.timelineLabel')}>
        <h2 className="text-[15px] font-semibold text-foreground">
          {t('requests.detail.timelineLabel')}
        </h2>
        <ol className="mt-4 space-y-4">
          <TimelineItem done title={t('requests.detail.stepSubmitted')} at={req.createdAt} />
          <TimelineItem
            done={reviewed}
            tone={req.status === 'rejected' ? 'danger' : 'default'}
            title={
              req.status === 'rejected'
                ? t('requests.detail.stepRejected')
                : req.status === 'approved'
                  ? t('requests.detail.stepApproved')
                  : t('requests.detail.stepAwaitingReview')
            }
            at={reviewed ? req.reviewedAt : null}
          />
          {req.offlineRequested && (
            <TimelineItem
              done
              title={t('requests.detail.stepOfflineRequested')}
              at={req.offlineRequestedAt}
            />
          )}
        </ol>
      </section>

      {/* Offline activation */}
      <section className="mt-8 rounded-lg border border-border p-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="space-y-1">
            <h2 className="flex items-center gap-2 text-[15px] font-semibold text-foreground">
              <Cable className="h-4 w-4" aria-hidden="true" />
              {t('requests.detail.offlineTitle')}
            </h2>
            <p className="max-w-[52ch] text-[13.5px] text-muted-foreground">
              {req.offlineRequested
                ? t('requests.detail.offlineRequestedHint')
                : t('requests.detail.offlineHint')}
            </p>
          </div>
          {req.offlineRequested ? (
            <Button variant="outline" disabled>
              <Check className="mr-2 h-4 w-4" aria-hidden="true" />
              {t('requests.detail.offlineRequested')}
            </Button>
          ) : (
            canRequestOffline && (
              <Button onClick={() => offlineMutation.mutate()} disabled={offlineMutation.isPending}>
                {offlineMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                )}
                {t('requests.detail.requestOffline')}
              </Button>
            )
          )}
        </div>
      </section>
    </div>
  );
}

export const handle = { crumb: 'Request' };
